import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGetSalesQuery, useGetSaleQuery, useReprintSaleMutation } from '../services/sales.service';
import { useGetUsersQuery } from '../services/users.service';
import { useGetLocationsQuery } from '../services/locations.service';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { formatCurrency } from '../utils/currency';

const SalesHistoryPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    cashierId: '',
    paymentMethod: '',
    status: '',
    locationId: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });
  const [detailsDialog, setDetailsDialog] = useState<string | null>(null);

  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const canViewAllSales = isAdmin || isManager;

  // Fetch filter options
  const { data: usersData } = useGetUsersQuery({ limit: 100, isActive: true });
  const { data: locationsData } = useGetLocationsQuery({ active: true });

  // Fetch sales
  const {
    data: salesResponse,
    isLoading,
    error,
    refetch
  } = useGetSalesQuery({
    ...filters,
    startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
    endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
    page: page + 1,
    limit: rowsPerPage
  });

  // Fetch sale details for dialog
  const { data: saleDetails } = useGetSaleQuery(detailsDialog || '', {
    skip: !detailsDialog
  });

  const [reprintSale] = useReprintSaleMutation();

  const sales = salesResponse?.sales || [];
  const pagination = salesResponse?.pagination;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      cashierId: '',
      paymentMethod: '',
      status: '',
      locationId: '',
      startDate: null,
      endDate: null,
    });
    setPage(0);
  };

  const handleReprint = async (saleId: string) => {
    try {
      const result = await reprintSale(saleId).unwrap();
      
      if (result.data) {
        // Generate thermal receipt for reprinting
        printThermalReceipt(result.data);
        alert('Recibo reimprinting successfully');
      }
    } catch (error: any) {
      console.error('Error reprinting sale:', error);
      let errorMessage = 'Error al reimprimir el recibo';
      
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  // Function to print thermal receipt (similar to SalesPage)
  const printThermalReceipt = (saleData: any) => {
    const customerLine = saleData.customerName ? `Cliente: ${saleData.customerName}\n` : '';
    const paymentMethodText = saleData.paymentMethod === 'CASH' ? 'Efectivo' : 
                                    saleData.paymentMethod === 'CARD' ? 'Tarjeta' : 
                                    saleData.paymentMethod === 'TRANSFER' ? 'Transferencia' : 
                                    saleData.paymentMethod === 'CHECK' ? 'Cheque' : 
                                    saleData.paymentMethod;
    
    const productLines = saleData.saleItems.map((item: any) => 
      `${item.product.name}\n   ${item.quantity} x ${formatCurrency(item.unitPrice)} = ${formatCurrency(item.total)}`
    ).join('\n');
    
    // Add discount information if present
    const discountLine = (saleData.discountAmount && saleData.discountAmount > 0) ? 
      `Descuento (${saleData.discountName || 'Sin nombre'}): -${formatCurrency(saleData.discountAmount)}` : '';
    
    const receiptContent = `REIMPRESIÓN DE RECIBO
============================
Fecha Original: ${new Date(saleData.createdAt).toLocaleString('es-HN')}
Reimprimir: ${new Date().toLocaleString('es-HN')}
${customerLine}Recibo No: ${saleData.id.slice(-8)}
----------------------------
PRODUCTOS:
${productLines}
----------------------------

Subtotal:     ${formatCurrency(saleData.subtotal)}${discountLine ? `\n${discountLine}` : ''}
ISV incluido: ${formatCurrency(saleData.tax)}
============================
TOTAL:        ${formatCurrency(saleData.total)}
============================

Método: ${paymentMethodText}
Cajero Original: ${saleData.user.name}

¡Gracias por su compra!
============================`;

    // Create print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Reimpresión de Recibo</title>
            <style>
              @media print {
                @page { margin: 0; }
                body { margin: 0; padding: 0; }
              }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                width: 58mm; 
                margin: 0; 
                padding: 0 2mm;
                line-height: 1.2;
                color: #000 !important;
              }
              * {
                color: #000 !important;
              }
              pre { 
                white-space: pre-wrap; 
                margin: 0; 
                padding: 0;
                font-family: inherit;
                color: #000 !important;
              }
            </style>
          </head>
          <body>
            <pre>${receiptContent}</pre>
            <script>
              window.onload = function() {
                window.print();
                setTimeout(() => window.close(), 1000);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const getPaymentMethodColor = (method: string): 'default' | 'success' | 'info' | 'warning' | 'secondary' => {
    switch (method) {
      case 'CASH': return 'success';
      case 'CARD': return 'info';
      case 'TRANSFER': return 'warning';
      case 'CHECK': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'error' => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  if (!canViewAllSales) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No tienes permisos para ver el historial de ventas
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error al cargar el historial de ventas
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Historial de Ventas
        </Typography>
        <Box>
          <Tooltip title="Actualizar">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Cajero</InputLabel>
              <Select
                value={filters.cashierId}
                onChange={(e) => setFilters({ ...filters, cashierId: e.target.value })}
                label="Cajero"
              >
                <MenuItem value="">Todos</MenuItem>
                {usersData?.users?.map((user: any) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {canViewAllSales && (
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tienda</InputLabel>
                <Select
                  value={filters.locationId}
                  onChange={(e) => setFilters({ ...filters, locationId: e.target.value })}
                  label="Tienda"
                >
                  <MenuItem value="">Todas las tiendas</MenuItem>
                  {locationsData?.data?.map((location: any) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de Pago</InputLabel>
              <Select
                value={filters.paymentMethod}
                onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
                label="Método de Pago"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="CASH">Efectivo</MenuItem>
                <MenuItem value="CARD">Tarjeta</MenuItem>
                <MenuItem value="TRANSFER">Transferencia</MenuItem>
                <MenuItem value="CHECK">Cheque</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="COMPLETED">Completada</MenuItem>
                <MenuItem value="CANCELLED">Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Inicio"
                value={filters.startDate}
                onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Fin"
                value={filters.endDate}
                onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 12, md: 1 }}>
            <Tooltip title="Limpiar filtros">
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                fullWidth
                size="small"
              >
                Limpiar
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
        mb: 2
      }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>No. Recibo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ubicación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cajero</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Método de Pago</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Descuento</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.map((sale) => (
                <TableRow key={sale.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(sale.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {sale.id.slice(-8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {sale.location?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {sale.user?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {sale.customerName || 'Cliente General'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        sale.paymentMethod === 'CASH' ? 'Efectivo' :
                        sale.paymentMethod === 'CARD' ? 'Tarjeta' :
                        sale.paymentMethod === 'TRANSFER' ? 'Transferencia' :
                        sale.paymentMethod === 'CHECK' ? 'Cheque' :
                        sale.paymentMethod
                      }
                      size="small"
                      color={getPaymentMethodColor(sale.paymentMethod)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium" color={sale.discountAmount && sale.discountAmount > 0 ? 'success.main' : 'text.secondary'}>
                      {sale.discountAmount && sale.discountAmount > 0 ? 
                        `-${formatCurrency(sale.discountAmount)}` : 
                        'Sin descuento'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(sale.total)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sale.status === 'COMPLETED' ? 'Completada' : 'Cancelada'}
                      size="small"
                      color={getStatusColor(sale.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          onClick={() => setDetailsDialog(sale.id)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(isAdmin || isManager) && (
                        <Tooltip title="Reimprimir recibo">
                          <IconButton
                            size="small"
                            onClick={() => handleReprint(sale.id)}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {pagination && (
        <Paper sx={{ mt: 1, p: 0 }}>
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            sx={{
              minHeight: '52px',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem'
              }
            }}
          />
        </Paper>
      )}

      {/* Sale Details Dialog */}
      <Dialog
        open={!!detailsDialog}
        onClose={() => setDetailsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles de la Venta
        </DialogTitle>
        <DialogContent dividers>
          {saleDetails && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">No. Recibo:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {saleDetails.data.id.slice(-8)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Fecha:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {format(new Date(saleDetails.data.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Ubicación:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {saleDetails.data.location?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Cajero:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {saleDetails.data.user?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Cliente:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {saleDetails.data.customerName || 'Cliente General'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Artículos:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Producto</TableCell>
                          <TableCell align="center">Cantidad</TableCell>
                          <TableCell align="right">Precio Unit.</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {saleDetails.data.saleItems.map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{item.product.name}</TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2">
                      Subtotal: {formatCurrency(saleDetails.data.subtotal)}
                    </Typography>
                    {saleDetails.data.discountAmount && saleDetails.data.discountAmount > 0 && (
                      <Typography variant="body2" color="success.main">
                        Descuento ({saleDetails.data.discountName || 'Sin nombre'}): -{formatCurrency(saleDetails.data.discountAmount)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Impuestos: {formatCurrency(saleDetails.data.tax)}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Total: {formatCurrency(saleDetails.data.total)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(isAdmin || isManager) && saleDetails && (
            <Button
              onClick={() => handleReprint(saleDetails.data.id)}
              startIcon={<PrintIcon />}
              variant="outlined"
            >
              Reimprimir
            </Button>
          )}
          <Button onClick={() => setDetailsDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesHistoryPage;